// Horaires affichés et saisis en Europe/Paris ; instants envoyés en ISO 8601 avec décalage (§13bis).
export const TZ = 'Europe/Paris';

const fmt = (opts: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('fr-FR', { timeZone: TZ, ...opts });
const fTime = fmt({ hour: '2-digit', minute: '2-digit' });
const fDay = fmt({ weekday: 'short', day: 'numeric', month: 'short' });
const fDayLong = fmt({ weekday: 'long', day: 'numeric', month: 'long' });
const fDate = fmt({ day: 'numeric', month: 'short', year: 'numeric' });
const parts = fmt({ year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });

export const toDate = (iso: string | null | undefined) => (iso ? new Date(iso) : null);

export function time(iso: string | null | undefined): string {
  const d = toDate(iso);
  return d ? fTime.format(d) : '—';
}
export function day(iso: string | null | undefined): string {
  const d = toDate(iso);
  return d ? fDay.format(d) : '—';
}
export function dayLong(iso: string | null | undefined): string {
  const d = toDate(iso);
  return d ? fDayLong.format(d) : '—';
}
export function date(iso: string | null | undefined): string {
  const d = toDate(iso);
  return d ? fDate.format(d) : '—';
}
export function dayTime(iso: string | null | undefined): string {
  const d = toDate(iso);
  if (!d) return '—';
  return isSameParisDay(d, new Date()) ? `aujourd’hui ${fTime.format(d)}` : `${fDay.format(d)} · ${fTime.format(d)}`;
}

function wall(d: Date) {
  const p = Object.fromEntries(parts.formatToParts(d).map((x) => [x.type, x.value]));
  return { y: +p.year, m: +p.month, d: +p.day, h: +p.hour, min: +p.minute, s: +p.second };
}
export function isSameParisDay(a: Date, b: Date) {
  const x = wall(a), y = wall(b);
  return x.y === y.y && x.m === y.m && x.d === y.d;
}
/** Date locale de Paris au format YYYY-MM-DD. */
export function parisDate(d: Date = new Date()): string {
  const w = wall(d);
  return `${w.y}-${String(w.m).padStart(2, '0')}-${String(w.d).padStart(2, '0')}`;
}
/** Minutes écoulées depuis minuit à Paris. */
export function parisMinutes(d: Date = new Date()): number {
  const w = wall(d);
  return w.h * 60 + w.min;
}
/** Jour de la semaine à Paris (0 = lundi). */
export function parisWeekdayIndex(d: Date = new Date()): number {
  const w = wall(d);
  const js = new Date(Date.UTC(w.y, w.m - 1, w.d)).getUTCDay();
  return (js + 6) % 7;
}

function offsetMinutes(utcMs: number): number {
  const w = wall(new Date(utcMs));
  return (Date.UTC(w.y, w.m - 1, w.d, w.h, w.min, w.s) - utcMs) / 60000;
}

/** "2026-11-14T09:00" (heure de Paris) → "2026-11-14T09:00:00+01:00". */
export function localInputToIso(value: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!m) return null;
  const naive = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  let off = offsetMinutes(naive);
  off = offsetMinutes(naive - off * 60000);
  const sign = off >= 0 ? '+' : '-';
  const a = Math.abs(off);
  return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:00${sign}${String(Math.floor(a / 60)).padStart(2, '0')}:${String(a % 60).padStart(2, '0')}`;
}

/** Instant ISO → valeur d'un <input type="datetime-local"> en heure de Paris. */
export function isoToLocalInput(iso: string | null | undefined): string {
  const d = toDate(iso);
  if (!d) return '';
  const w = wall(d);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${w.y}-${p(w.m)}-${p(w.d)}T${p(w.h)}:${p(w.min)}`;
}

/** Instant courant en UTC (le suffixe Z vaut un décalage explicite). */
export function nowIso(): string {
  return new Date().toISOString();
}

export function minutesSince(iso: string | null | undefined, now = Date.now()): number | null {
  const d = toDate(iso);
  return d ? (now - d.getTime()) / 60000 : null;
}

export function ago(iso: string | null | undefined): string {
  const m = minutesSince(iso);
  if (m === null) return '—';
  if (m < 1) return 'à l’instant';
  if (m < 60) return `il y a ${Math.round(m)} min`;
  if (m < 60 * 24) return `il y a ${Math.floor(m / 60)} h ${String(Math.round(m % 60)).padStart(2, '0')}`;
  return `le ${dayTime(iso)}`;
}

export function duration(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return '—';
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return h ? `${h} h ${String(m).padStart(2, '0')}` : `${m} min`;
}

export const hhmm = (min: number) => `${String(Math.floor(min / 60) % 24).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
export const parseHHMM = (s: string) => {
  const m = /^(\d{2}):(\d{2})$/.exec(s);
  return m ? +m[1] * 60 + +m[2] : null;
};

/** Fraîcheur d'une sonde (REQ-25) : périmée au-delà de 2 min, perdue à partir de 15 min. */
export function readingFreshness(measuredAt: string | null | undefined, lostAfter = 15): 'fresh' | 'stale' | 'lost' | 'none' {
  const m = minutesSince(measuredAt);
  if (m === null) return 'none';
  if (m >= lostAfter) return 'lost';
  if (m > 2) return 'stale';
  return 'fresh';
}
